import React, { useState } from "react";
import { Download } from "lucide-react";
import ExcelJS from "exceljs";
import styles from "@/pages/Dashboard/Clientes/Clientes.module.css";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import { ProductApi } from "@/utils/models/Product";

const DownloadExampleXlsx: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const userContext = useUser();
  const userData = userContext?.userData;

  const fetchProducts = async (): Promise<ProductApi[]> => {
    try {
      const resp = await api.get('/product', {
        params: { page: 1, pageSize: 500 },
        headers: { Authorization: `Bearer ${userData?.auth}` },
      });

      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;

      let list: ProductApi[] = [];
      if (Array.isArray(raw.items)) {
        list = raw.items;
      } else if (Array.isArray(raw)) {
        list = raw;
      } else {
        const maybeArray = Object.values(raw).find(v => Array.isArray(v));
        if (Array.isArray(maybeArray)) list = maybeArray as ProductApi[];
      }
      return list;
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      return [];
    }
  };

  const generateExampleXlsx = async () => {
    setLoading(true);
    try {
      const products = await fetchProducts();
      const productNames = products.map(p => p.name).filter(Boolean);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'CoWonder';
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet('Clientes', {
        views: [{ state: 'frozen', ySplit: 1 }]
      });

      // Define columns with headers and widths
      const columns = [
        { header: 'name', key: 'name', width: 30, description: 'Nome empresarial' },
        { header: 'email', key: 'email', width: 35, description: 'Email (obrigatório)' },
        { header: 'phone', key: 'phone', width: 18, description: 'Telefone (obrigatório)' },
        { header: 'companyDocument', key: 'companyDocument', width: 20, description: 'CNPJ da Empresa' },
        { header: 'postalBoxNumber', key: 'postalBoxNumber', width: 15, description: 'Caixa Postal' },
        { header: 'legalRepresentativeName', key: 'legalRepresentativeName', width: 30, description: 'Representante Legal (obrigatório)' },
        { header: 'legalRepresentativeDocument', key: 'legalRepresentativeDocument', width: 25, description: 'CPF do Representante (opcional)' },
        { header: 'birthDate', key: 'birthDate', width: 15, description: 'Data de Nascimento YYYY-MM-DD (opcional)' },
        { header: 'address', key: 'address', width: 40, description: 'Endereço do cliente (opcional)' },
        { header: 'productName', key: 'productName', width: 25, description: 'Produto (obrigatório)' },
        { header: 'startDate', key: 'startDate', width: 15, description: 'Data de Início YYYY-MM-DD (obrigatório)' },
        { header: 'endDate', key: 'endDate', width: 15, description: 'Data de Término YYYY-MM-DD (obrigatório)' },
        { header: 'affiliateId', key: 'affiliateId', width: 40, description: 'ID do Afiliado (opcional)' },
      ];

      worksheet.columns = columns.map(col => ({
        header: col.header,
        key: col.key,
        width: col.width,
      }));

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF7E22CE' }, // Purple background
        };
        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' }, // White text
          size: 11,
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF5B21B6' } },
          left: { style: 'thin', color: { argb: 'FF5B21B6' } },
          bottom: { style: 'thin', color: { argb: 'FF5B21B6' } },
          right: { style: 'thin', color: { argb: 'FF5B21B6' } },
        };
      });
      headerRow.height = 25;

      // Add description row (row 2) with field descriptions
      const descriptionRow = worksheet.addRow(
        columns.map(col => col.description)
      );
      descriptionRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF3E8FF' }, // Light purple background
        };
        cell.font = {
          italic: true,
          color: { argb: 'FF6B21A8' }, // Purple text
          size: 10,
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
          wrapText: true,
        };
      });
      descriptionRow.height = 30;

      // Add example data row (row 3)
      const today = new Date();
      const nextYear = new Date(today);
      nextYear.setFullYear(nextYear.getFullYear() + 1);

      const formatDate = (d: Date) => d.toISOString().split('T')[0];

      const exampleData = {
        name: 'Empresa Exemplo LTDA',
        email: 'contato@empresaexemplo.com.br',
        phone: '(11)99999-8888',
        companyDocument: '12345678000199',
        postalBoxNumber: '1234',
        legalRepresentativeName: 'João da Silva',
        legalRepresentativeDocument: '12345678900',
        birthDate: '1990-05-15',
        address: 'Rua das Flores, 123 - Centro',
        productName: productNames[0] || 'Nome do Produto',
        startDate: formatDate(today),
        endDate: formatDate(nextYear),
        affiliateId: '',
      };

      const exampleRow = worksheet.addRow(exampleData);
      exampleRow.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        };
      });

      // Add data validation for productName column (column J, index 10 - after adding birthDate and address)
      if (productNames.length > 0) {
        const productColumn = 10; // productName is the 10th column

        // Apply data validation to rows 3-1000 (leaving room for many entries)
        for (let row = 3; row <= 1000; row++) {
          worksheet.getCell(row, productColumn).dataValidation = {
            type: 'list',
            allowBlank: false,
            formulae: [`"${productNames.join(',')}"`],
            showErrorMessage: true,
            errorTitle: 'Produto Inválido',
            error: 'Por favor, selecione um produto da lista.',
            showInputMessage: true,
            promptTitle: 'Selecione o Produto',
            prompt: 'Escolha um produto disponível no seu coworking.',
          };
        }
      }

      // Add instructions worksheet
      const instructionsSheet = workbook.addWorksheet('Instruções');
      instructionsSheet.columns = [
        { header: 'Campo', key: 'campo', width: 30 },
        { header: 'Descrição', key: 'descricao', width: 50 },
        { header: 'Obrigatório', key: 'obrigatorio', width: 15 },
        { header: 'Formato', key: 'formato', width: 30 },
      ];

      // Style instructions header
      const instrHeaderRow = instructionsSheet.getRow(1);
      instrHeaderRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF7E22CE' },
        };
        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' },
          size: 11,
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      instrHeaderRow.height = 25;

      const instructions = [
        { campo: 'name', descricao: 'Nome empresarial da empresa cliente', obrigatorio: 'Não', formato: 'Texto livre' },
        { campo: 'email', descricao: 'Email de contato do cliente', obrigatorio: 'Sim', formato: 'email@exemplo.com' },
        { campo: 'phone', descricao: 'Telefone de contato', obrigatorio: 'Sim', formato: 'Aceita (11)99999-8888, 11999998888 ou +55 11 99999-8888' },
        { campo: 'companyDocument', descricao: 'CNPJ da empresa', obrigatorio: 'Não', formato: 'Apenas números (14 dígitos)' },
        { campo: 'postalBoxNumber', descricao: 'Número da caixa postal', obrigatorio: 'Não', formato: 'Texto/Número' },
        { campo: 'legalRepresentativeName', descricao: 'Nome do representante legal', obrigatorio: 'Sim', formato: 'Texto livre' },
        { campo: 'legalRepresentativeDocument', descricao: 'CPF do representante legal', obrigatorio: 'Não', formato: 'Apenas números (11 dígitos)' },
        { campo: 'birthDate', descricao: 'Data de nascimento do cliente', obrigatorio: 'Não', formato: 'YYYY-MM-DD (ex: 1990-05-15) ou DD-MM-YYYY (ex: 15-05-1990)' },
        { campo: 'address', descricao: 'Endereço do cliente', obrigatorio: 'Não', formato: 'Texto livre' },
        { campo: 'productName', descricao: 'Nome do produto/plano contratado', obrigatorio: 'Sim', formato: 'Selecione da lista disponível' },
        { campo: 'startDate', descricao: 'Data de início do contrato', obrigatorio: 'Sim', formato: 'YYYY-MM-DD (ex: 2024-01-15) ou DD-MM-YYYY (ex: 15-01-2024)' },
        { campo: 'endDate', descricao: 'Data de término do contrato', obrigatorio: 'Sim', formato: 'YYYY-MM-DD (ex: 2025-01-15) ou DD-MM-YYYY (ex: 15-01-2025)' },
        { campo: 'affiliateId', descricao: 'ID do cliente que indicou (afiliado)', obrigatorio: 'Não', formato: 'UUID do cliente afiliado' },
      ];

      instructions.forEach((instr, idx) => {
        const row = instructionsSheet.addRow(instr);
        row.eachCell((cell, colNumber) => {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          if (colNumber === 3) {
            cell.font = {
              bold: instr.obrigatorio === 'Sim',
              color: { argb: instr.obrigatorio === 'Sim' ? 'FFE53935' : 'FF4B5563' },
            };
          }
        });
        // Alternate row colors
        if (idx % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF9FAFB' },
            };
          });
        }
      });

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'exemplo_importacao_clientes.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erro ao gerar arquivo XLSX:', error);
      window.alert('Erro ao gerar arquivo de exemplo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className={styles.addButton}
      onClick={generateExampleXlsx}
      disabled={loading}
    >
      <Download size={16} />
      {loading ? 'Gerando...' : 'Baixar Exemplo'}
    </button>
  );
};

export default DownloadExampleXlsx;

